/**
 * Form Design Templates API — reusable, org-scoped visual themes for Smart Forms.
 *
 * Backs the Design Template Library manager and the form builder's Design tab.
 * All calls are org-scoped via the X-CBO-ID header (active_cbo_id), matching the
 * rest of services/api/*. The `theme` shape is defined in
 * components/forms/formTheme.ts (PartialFormTheme).
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
import type { PartialFormTheme, FormTheme } from '@/components/forms/formTheme';

export interface FormDesignTemplate {
  id: number;
  cbo_id: number;
  name: string;
  description?: string | null;
  theme: PartialFormTheme;
  is_default: boolean;
  is_active: boolean;
  created_by?: number | null;
  created_ts?: string | null;
  last_updated_ts?: string | null;
  /** Present on single-get: the defaults-merged theme, ready to preview. */
  resolved_theme?: FormTheme;
}

export interface FormDesignTemplateCreate {
  name: string;
  description?: string | null;
  theme?: PartialFormTheme;
  is_default?: boolean;
}

export interface FormDesignTemplateUpdate {
  name?: string;
  description?: string | null;
  theme?: PartialFormTheme;
  is_default?: boolean;
}

class FormDesignTemplatesAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async list(): Promise<FormDesignTemplate[]> {
    const res = await this.client.get<FormDesignTemplate[]>('/api/form-design-templates');
    return res.data;
  }

  async get(id: number): Promise<FormDesignTemplate> {
    const res = await this.client.get<FormDesignTemplate>(`/api/form-design-templates/${id}`);
    return res.data;
  }

  async create(data: FormDesignTemplateCreate): Promise<FormDesignTemplate> {
    const res = await this.client.post<FormDesignTemplate>('/api/form-design-templates', data);
    return res.data;
  }

  async update(id: number, data: FormDesignTemplateUpdate): Promise<FormDesignTemplate> {
    const res = await this.client.put<FormDesignTemplate>(`/api/form-design-templates/${id}`, data);
    return res.data;
  }

  async remove(id: number): Promise<void> {
    await this.client.delete(`/api/form-design-templates/${id}`);
  }

  async setDefault(id: number): Promise<FormDesignTemplate> {
    const res = await this.client.post<FormDesignTemplate>(`/api/form-design-templates/${id}/set-default`, {});
    return res.data;
  }

  /**
   * The fully-resolved theme to render a form with, for an authenticated
   * surface. Mirrors the public form's resolution order (explicit design
   * template -> org default template -> org branding -> defaults), so staff
   * see in-app exactly what a member sees on the public link.
   */
  async resolved(designTemplateId?: number | null): Promise<FormTheme> {
    const res = await this.client.get<FormTheme>('/api/form-design-templates/resolved', {
      params: designTemplateId != null ? { design_template_id: designTemplateId } : undefined,
    });
    return res.data;
  }

  /** Build (not persist) a theme from the org's brand settings. */
  async importFromBranding(): Promise<{ theme: PartialFormTheme; resolved_theme: FormTheme }> {
    const res = await this.client.post('/api/form-design-templates/import-from-branding', {});
    return res.data;
  }

  /** Upload a logo or background image; returns a public URL to store in the theme. */
  async uploadAsset(file: File): Promise<{ url: string }> {
    const form = new FormData();
    form.append('file', file);
    // Clear the instance default 'application/json' so axios sets
    // multipart/form-data *with its boundary* from the FormData body. Sending a
    // Content-Type without a boundary makes the server fail to parse the file
    // (the upload silently fails).
    const res = await this.client.post('/api/form-design-templates/assets', form, {
      headers: { 'Content-Type': undefined },
    });
    return res.data;
  }
}

export const formDesignTemplatesApi = new FormDesignTemplatesAPI();
export default formDesignTemplatesApi;
